/**
 * Hero v2 — category filmstrip with per-category background crossfade.
 *
 * Motion transcribed from the Figma prototype (file HI4NmhKlZUfNBxemEQcZES):
 *   auto-rotate  AFTER_TIMEOUT 2.4s -> Smart Animate, "Gentle" spring, 1.022s
 *   tab click    ON_CLICK           -> Smart Animate, Ease Out, 0.3s
 *   entrance     copy slides in 0.3s after mount, Ease Out, 0.3s
 *
 * The dwell timer is measured from the start of each transition, matching
 * Figma's AFTER_TIMEOUT semantics (the timer starts when the variant is entered).
 */

const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');

class HeroV2 extends HTMLElement {
  #index = 0;
  #timer = null;
  #entranceTimer = null;
  #swapTimer = null;
  #observer = null;
  #idleTimer = null;
  #bound = false;

  /**
   * Pause reasons, tracked independently so one source cannot clear another's
   * pause. Deliberately does NOT include hovering: the cursor crosses a
   * full-bleed hero constantly on its way elsewhere, so hover-pause made the
   * rotation feel broken. The reasons are:
   *
   *   'driving'  — the visitor just used a tab; cleared after resumeDelay of
   *                no further interaction.
   *   'focus'    — keyboard focus is inside the hero. Never auto-expires: an
   *                auto-advancing tablist is unusable for a keyboard visitor,
   *                and WCAG 2.2.2 (Pause, Stop, Hide) leans on this one.
   *   'offscreen'— scrolled out of the viewport.
   *   'hidden'   — the browser tab is in the background.
   *
   * @type {Set<string>}
   */
  #pauseReasons = new Set();

  connectedCallback() {
    this.track = this.querySelector('[data-track]');
    this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
    this.panels = Array.from(this.querySelectorAll('.hero-v2__panel'));
    this.backgrounds = Array.from(this.querySelectorAll('.hero-v2__bg'));
    this.tabsViewport = this.querySelector('[data-tabs]');
    this.cta = this.querySelector('[data-cta]');
    this.ctaLabel = this.querySelector('[data-cta-label]');

    this.count = this.panels.length;
    // Durations are authored in milliseconds (Shopify range steps must be integers).
    this.interval = parseFloat(this.dataset.interval) || 2400;
    this.duration = parseFloat(this.dataset.duration) || 1020;
    this.entranceDelay = parseFloat(this.dataset.entranceDelay) || 0;
    // How long after the visitor's last tab interaction autoplay resumes.
    this.resumeDelay = Number(this.dataset.resumeDelay ?? 5000);
    this.autoplay = this.dataset.autoplay === 'true';

    this.style.setProperty('--hv2-active', '0');
    this.#index = 0;
    this.#applyState(0);

    // Listeners on this element and its children survive a detach/re-attach, so
    // they must only ever be bound once — connectedCallback fires again whenever
    // the theme editor re-inserts the section.
    if (!this.#bound) {
      this.#bindElement();
      this.#bound = true;
    }
    // Listeners on document/matchMedia are torn down on disconnect, so they are
    // re-bound on every connect.
    this.#bindGlobal();

    this.#reveal();
    this.#watchVisibility();
    this.#schedule();
  }

  disconnectedCallback() {
    this.#stop();
    clearTimeout(this.#entranceTimer);
    clearTimeout(this.#swapTimer);
    clearTimeout(this.#idleTimer);
    this.#observer?.disconnect();
    this.#pauseReasons.clear();
    document.removeEventListener('visibilitychange', this.onVisibility);
    document.removeEventListener('shopify:block:select', this.onBlockSelect);
    REDUCED_MOTION.removeEventListener('change', this.onMotionPref);
  }

  #bindGlobal() {
    this.onVisibility = () => this.#pause(document.hidden, 'hidden');
    document.addEventListener('visibilitychange', this.onVisibility);

    // Theme editor: jump to the category a merchant selects in the sidebar.
    this.onBlockSelect = (event) => {
      if (!this.contains(event.target)) return;
      const panel = event.target.closest('.hero-v2__panel');
      if (panel) this.goTo(Number(panel.dataset.index), { motion: 'tap' });
    };
    document.addEventListener('shopify:block:select', this.onBlockSelect);

    // A runtime reduced-motion change should stop an already-running rotation.
    this.onMotionPref = () => (REDUCED_MOTION.matches ? this.#stop() : this.#schedule());
    REDUCED_MOTION.addEventListener('change', this.onMotionPref);
  }

  #bindElement() {
    this.tabs.forEach((tab) => {
      tab.addEventListener('click', () => {
        this.#noteInteraction();
        this.goTo(Number(tab.dataset.index), { motion: 'tap' });
      });
    });

    this.querySelector('[data-rail]')?.addEventListener('keydown', (event) => {
      const delta = { ArrowRight: 1, ArrowLeft: -1, Home: -Infinity, End: Infinity }[event.key];
      if (delta === undefined) return;
      event.preventDefault();
      const next =
        delta === -Infinity
          ? 0
          : delta === Infinity
            ? this.count - 1
            : (this.#index + delta + this.count) % this.count;
      this.#noteInteraction();
      this.goTo(next, { motion: 'tap' });
      this.tabs[next]?.focus();
    });

    // No pointerenter/pointerleave pause on purpose — see #pauseReasons.
    this.addEventListener('focusin', () => this.#pause(true, 'focus'));
    this.addEventListener('focusout', () => {
      if (!this.contains(document.activeElement)) this.#pause(false, 'focus');
    });
  }

  /**
   * The visitor is driving the carousel. Hold autoplay off, and restart the
   * countdown on every interaction so a run of tab clicks does not let autoplay
   * cut in between them.
   */
  #noteInteraction() {
    this.#pause(true, 'driving');
    clearTimeout(this.#idleTimer);
    this.#idleTimer = setTimeout(() => {
      this.#pauseReasons.delete('driving');
      // Advance straight away rather than re-arming and then sitting through a
      // further dwell, so movement resumes at resumeDelay rather than
      // resumeDelay + interval.
      if (this.#canRun()) this.goTo(this.#index + 1, { motion: 'auto' });
      else this.#schedule();
    }, this.resumeDelay);
  }

  #canRun() {
    return this.autoplay && !this.#pauseReasons.size && this.count >= 2 && !REDUCED_MOTION.matches;
  }

  #reveal() {
    this.#entranceTimer = setTimeout(() => {
      requestAnimationFrame(() => this.classList.add('is-revealed'));
    }, 20);
  }

  #watchVisibility() {
    if (!('IntersectionObserver' in window)) return;
    this.#observer = new IntersectionObserver(
      ([entry]) => this.#pause(!entry.isIntersecting),
      { threshold: 0 }
    );
    this.#observer.observe(this);
  }

  /**
   * @param {boolean} value
   * @param {'driving' | 'focus' | 'hidden' | 'offscreen'} reason
   */
  #pause(value, reason) {
    if (value) this.#pauseReasons.add(reason);
    else this.#pauseReasons.delete(reason);

    if (this.#pauseReasons.size) this.#stop();
    else this.#schedule();
  }

  #stop() {
    clearTimeout(this.#timer);
    this.#timer = null;
  }

  #schedule() {
    this.#stop();
    if (!this.#canRun()) return;
    this.#timer = setTimeout(() => this.goTo(this.#index + 1, { motion: 'auto' }), this.interval);
  }

  /**
   * @param {number} index
   * @param {{ motion?: 'auto' | 'tap' }} [options]
   */
  goTo(index, { motion = 'auto' } = {}) {
    if (!this.count) return;
    const next = ((index % this.count) + this.count) % this.count;

    this.dataset.motion = motion;

    if (next !== this.#index) {
      this.#index = next;
      this.style.setProperty('--hv2-active', String(next));
      this.#applyState(next);
    }

    this.#schedule();
  }

  #applyState(index) {
    this.tabs.forEach((tab, i) => {
      const active = i === index;
      tab.classList.toggle('is-active', active);
      tab.setAttribute('aria-selected', String(active));
      tab.tabIndex = active ? 0 : -1;
    });

    this.panels.forEach((panel, i) => {
      panel.toggleAttribute('aria-hidden', i !== index);
    });

    this.backgrounds.forEach((bg, i) => {
      bg.classList.toggle('is-active', i === index);
    });

    this.#scrollTabIntoView(index);
    this.#swapCta(index);
  }

  #scrollTabIntoView(index) {
    const viewport = this.tabsViewport;
    const tab = this.tabs[index];
    if (!viewport || !tab || viewport.scrollWidth <= viewport.clientWidth) return;

    const target = tab.offsetLeft - (viewport.clientWidth - tab.offsetWidth) / 2;
    viewport.scrollTo({
      left: Math.max(0, target),
      behavior: REDUCED_MOTION.matches ? 'auto' : 'smooth',
    });
  }

  #swapCta(index) {
    if (!this.cta || !this.ctaLabel) return;

    const tab = this.tabs[index];
    const label = tab?.dataset.ctaLabel || this.cta.dataset.defaultLabel;
    const href = tab?.dataset.ctaHref || this.cta.dataset.defaultHref;

    if (this.ctaLabel.textContent.trim() === label.trim()) {
      if (href) this.cta.href = href;
      return;
    }

    clearTimeout(this.#swapTimer);
    this.ctaLabel.classList.add('is-swapping');
    this.#swapTimer = setTimeout(
      () => {
        this.ctaLabel.textContent = label;
        if (href) this.cta.href = href;
        this.ctaLabel.classList.remove('is-swapping');
      },
      REDUCED_MOTION.matches ? 0 : this.duration / 3
    );
  }
}

if (!customElements.get('hero-v2')) {
  customElements.define('hero-v2', HeroV2);
}
